import { useState } from "react";
import { useAuth } from "../../../context/contextAuth";
import { getIpResults } from "../../../api/get/gcp/get-ip";
import { scanGceIp } from "../../../api/post/post-gcp";

const useGcpIp = () => {
  const { userId, user } = useAuth();

  // MAIN STATES
  const [networkLoading, setNetworkLoading] = useState(false);
  const [ip, setIp] = useState("");
  const [error, setError] = useState(null);

  // RESULTS FROM BACKEND
  const [networkResults, setNetworkResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // TABLES FOR UI
  const [ipVulTable, setIpVulTable] = useState([]);
  const [ipEpssTable, setIpEpssTable] = useState([]);
  const [ipServices, setIpServices] = useState([]);

  // SCORE DATA
  const [TVulScore, setTVulScore] = useState(0);
  const [CVulScore, setCVulScore] = useState(0);
  const [saveScore, setSaveScore] = useState(0);
  const [auditResult, setAuditResult] = useState([]);

  // DASHBOARD CONTROL FLAGS
  const [ipScanLoading, setIpScanLoading] = useState(false);
  const [ipDashboardView, setIpDashboardView] = useState(false);
  const [ipHeader, setIpHeader] = useState(false);

  // -----------------------------
  // INIT DATA
  // -----------------------------
  const initData = async () => {
    if (!user) {
      resetState();
      return;
    }

    await fetchNetworkResults(userId);
    await loadNetworkData(0);
  };

  // -----------------------------
  // FETCH RESULTS (one endpoint only)
  // -----------------------------
  const fetchNetworkResults = async (userId) => {
    try {
      const results = await getIpResults(userId);
      setNetworkResults(results);
      console.log("Network results:", results);
    } catch (err) {
      console.error("Error fetching network results:", err);
      setNetworkResults([]);
    }
  };

  // -----------------------------
  // LOAD SINGLE SCAN INTO UI
  // -----------------------------
  const loadNetworkData = async (index) => {
    const scan = networkResults[index];
    if (!scan) return;

    setCurrentIndex(index);

    setIpVulTable(scan.vuln_data || []);
    setIpEpssTable(scan.epss_data || []);
    setIpServices(scan.services || []);

    setTVulScore(scan.t_vul);
    setCVulScore(scan.c_vul);
    setSaveScore(scan.ss);

    // Extract solutions list from vulnerabilities
    const solutions = (scan.vuln_data || [])
      .map(v => v.solution)
      .filter(Boolean);

    setAuditResult(solutions);
  };

  // -----------------------------
  // SUBMIT (PUBLIC / PRIVATE)
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIpDashboardView(true);
    setIpScanLoading(true);
    setIpHeader(false);

    try {
      await scanGceIp(userId, ip);

      await fetchNetworkResults(userId);
      await loadNetworkData(0);

    } catch (err) {
      console.log(err.message);
      setIpDashboardView(false);
      setError(err.message);
    } finally {
      setIpScanLoading(false);
      setIpHeader(true);
      setIp("");
      setTimeout(() => setError(null), 6000);
    }
  };

  // -----------------------------
  // RESET CONTEXT
  // -----------------------------
  const resetState = () => {
    setNetworkLoading(false);
    setIp("");
    setError(null);

    setNetworkResults([]);
    setCurrentIndex(0);

    setIpVulTable([]);
    setIpEpssTable([]);
    setIpServices([]);

    setTVulScore(0);
    setCVulScore(0);
    setSaveScore(0);
    setAuditResult([]);

    setIpScanLoading(false);
    setIpDashboardView(false);
    setIpHeader(false);
  };

  return {
    // STATE
    networkLoading,
    ip,
    error,

    networkResults,
    currentIndex,

    ipVulTable,
    ipEpssTable,
    ipServices,

    TVulScore,
    CVulScore,
    saveScore,
    auditResult,

    ipScanLoading,
    ipDashboardView,
    ipHeader,

    // SETTERS
    setIp,
    setCurrentIndex,
    setIpDashboardView,
    setIpHeader,

    // FUNCTIONS
    initData,
    loadNetworkData,
    handleSubmit,
    resetState,
  };
};

export default useGcpIp;
